import { Grid, Skeleton, Typography, useTheme } from '@mui/material';
import React from 'react';
import Flex from 'ui/Flex/Flex';
import { currencyFormatter, formatFloatValue } from 'helpers/formatHelper';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import { EnveloppesData } from '../helpers/retrospectiveData';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import {
    StyBlackBoldText,
    StyText,
    StyPrimaryText,
} from '../helpers/StyTypography';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const StyDisplay = ({
    text,
    amount,
    etp,
    component,
}: {
    text: string;
    amount: number;
    etp: number;
    component: string;
}): React.ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();
    const hasNoAmount = amount === 0 && etp === 0;
    return (
        <StyPaper
            sx={{
                height: '100%',
                padding: '1rem',
            }}
        >
            <Grid
                container
                gap={2}
                height="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Flex
                    direction="column"
                    height="100%"
                    width="100%"
                    justifyContent="space-between"
                >
                    <Grid item xs={12} height="1rem">
                        <StyBlackBoldText>{text}</StyBlackBoldText>
                    </Grid>
                    {hasNoAmount && component === 'remplacement' ? (
                        <Flex height="100%" marginTop="-2rem">
                            <FlashOnIcon fontSize="large" color="primary" />
                            <Typography>
                                {t(
                                    'humanResources.retrospective.envelopes.noDataPlaceholder',
                                )
                                    .split(' ')
                                    .map((w, index) => {
                                        if (w === 'personnalisation') {
                                            return (
                                                <Link
                                                    key={index}
                                                    to="/human_resources/settings"
                                                    style={{
                                                        textDecoration: 'none',
                                                        color: theme.palette
                                                            .primary.main,
                                                    }}
                                                >
                                                    {w}
                                                </Link>
                                            );
                                        }
                                        return w + ' ';
                                    })}
                            </Typography>
                        </Flex>
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <Flex justifyContent="space-between">
                                    <StyText>
                                        {t(
                                            'humanResources.retrospective.envelopes.remuneratedTotal',
                                        )}
                                    </StyText>
                                    <StyPrimaryText>
                                        {currencyFormatter(amount)}
                                    </StyPrimaryText>
                                </Flex>
                            </Grid>
                            <Grid item xs={12}>
                                <Flex justifyContent="space-between">
                                    <StyText>{t('humanResources.etp')}</StyText>
                                    <StyPrimaryText>
                                        {formatFloatValue(etp)}
                                    </StyPrimaryText>
                                </Flex>
                            </Grid>
                        </>
                    )}
                </Flex>
            </Grid>
        </StyPaper>
    );
};

const AmountLoader = () => (
    <Skeleton variant="rectangular" width="100%" height="100%" />
);
export const AbsentAndSeasonalEmployeesCost = ({
    data,
    isLoading,
}: {
    data: EnveloppesData;
    isLoading: boolean;
}): React.ReactElement => {
    const { t } = useTranslation();
    return (
        <Grid container minHeight="25rem" spacing={2}>
            <Grid item xs={12}>
                <Flex
                    justifyContent="space-between"
                    direction="column"
                    height="100%"
                >
                    {isLoading ? (
                        <Flex
                            gap={2}
                            direction="column"
                            height="100%"
                            width="100%"
                        >
                            <AmountLoader />
                            <AmountLoader />
                        </Flex>
                    ) : (
                        <Flex
                            gap={2}
                            direction="column"
                            sx={{
                                marginBottom: '0',
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <StyDisplay
                                text={t(
                                    'humanResources.retrospective.envelopes.absentPersonalReplacement',
                                )}
                                amount={data.absenceCost}
                                etp={data.absenceEtp}
                                component="remplacement"
                            />
                            <StyDisplay
                                text={t(
                                    'humanResources.retrospective.envelopes.seasonalEmployeesEnveloppes',
                                )}
                                amount={data.seasonalCost}
                                etp={data.seasonalEtp}
                                component="saisonnier"
                            />
                        </Flex>
                    )}
                </Flex>
            </Grid>
        </Grid>
    );
};
